import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useLocale } from "lib/hooks/useLocale";
import { usePaymentContext } from "lib/contexts/PaymentProvider";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { getAutomationPayloadForMethod, isMobile, redirectUrl } from "lib/utils/helpers";
import { analyticsTypes, IMAGE_CDN_URL } from "lib/utils/constants";
import { useIsSubscribeMode } from "lib/hooks/useIsSubscribeMode";
import { getUpiAppLink, handlePaymentError, parseMethodUiAttributes } from "lib/utils/payments";
import { analyticsEvents } from "lib/utils/constants";
import useForm, { FormField } from "lib/hooks/useForm";
import { getPaymentMethodAction } from "lib/core/apiMethods";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useMethod } from "components/payments/methods/useMethod";
import { ParsedColor, PaymentMode, UpiIntentApps } from "lib/types/payments";
import { errorToast } from "lib/utils/toasters";
import { currencyFormatter } from "lib/utils/formatters";
import { DialogBody, DialogHeader, GenericDialog } from "components/common/dialogs/GenericDialog";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — react-qr-code types declare only default export but CJS exports named QRCode
import { QRCode } from "react-qr-code";
import BlurredQRImage from "assests/images/blurred_qr.png";
import CircularSpinner from "components/common/loaders/CircularSpinner";
import OfferBadge from "components/common/OfferBadge";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { getItems } from "lib/utils/checkout";
import OverlaySpinner from "components/common/loaders/OverlaySpinner";
import { usePopupOrchestrator } from "lib/contexts/PopupOrchestratorProvider";
import { UPI_INTENT_APPS as apps, UpiIntentItems, MethodLogo } from "@shopflo/ui/payments/upi-intent";
import { AppsGrid, getTextColorByBrightness, hexToRgba } from "@shopflo/ui";
import SuggestedMethodWrapper from "components/common/SuggestedMethodWrapper";

export const UPICollect = ({ modeData }: { modeData: PaymentMode }) => {
  const { t } = useLocale();
  const { openPopup } = usePopupOrchestrator();
  const { sendAnalyticsEvent } = useSendAnalyticsEvent();
  const [isLoading, setIsLoading] = useState(false);
  const [generatedQRCode, setGeneratedQRCode] = useState<string | null>(null);
  const timer = useRef<NodeJS.Timeout | null>(null);

  const {
    state: { paymentMethods, isPolling },
    actions: { setPaymentState },
  } = usePaymentContext();

  const {
    state: { checkoutId },
    actions: { setCheckoutExpired, setOOSItems },
  } = useCheckoutContext();

  const {
    state: { checkoutMetadata, merchant },
  } = useMerchantContext();

  const isSubscribeMode = useIsSubscribeMode();

  const methodUiAttributes = useMemo(() => {
    return parseMethodUiAttributes({ mode: modeData, checkoutMetadata, isSubscribeMode });
  }, [modeData, checkoutMetadata, isSubscribeMode]);

  const parsedColors: ParsedColor = {
    backgroundColor: methodUiAttributes.paymentCard.backgroundColor,
    borderColor: methodUiAttributes.paymentCard.borderColor,
    iconColor: methodUiAttributes.paymentCard.iconColor,
    subtitleColor: methodUiAttributes.paymentCard.subtitleColor,
    textColor: methodUiAttributes.paymentCard.textColor,
  };

  const { hasDiscountOrFreebie, offerText } = useMethod({ modeData, inFocus: false });

  const [timeLeft, setTimeLeft] = useState<number>(15 * 60); // 15 minutes in seconds
  const [isOtherAppsOpen, setIsOtherAppsOpen] = useState(false);

  const upiModeConfig = methodUiAttributes.modeConfig;

  const upiApps: UpiIntentItems[] = useMemo(() => {
    return upiModeConfig?.selected_apps?.filter((item) => item.is_active)?.length
      ? upiModeConfig?.selected_apps?.filter((item) => item.is_active)
      : apps?.selected_apps;
  }, [upiModeConfig]);

  const otherApps: UpiIntentItems[] = useMemo(() => {
    // When rolling this feature out, ideally the merchant's ui-attribute will not have the modeConfig key inside the UPI_INTENT tile - in that case we fallback to the default other apps
    return upiModeConfig?.other_apps
      ? upiModeConfig?.other_apps?.filter((item) => item.is_active) ?? []
      : apps.other_apps;
  }, [upiModeConfig]);

  const hasUpiIntentMode = paymentMethods?.find((e: PaymentMode) => e.name === "UPI_INTENT");
  const isUpiIntentModeAvailable = hasUpiIntentMode?.isAvailable;

  const upiAmount = useMemo(() => {
    return modeData?.amount;
  }, [modeData]);

  const {
    handleSubmit,
    setErrors,
    inputProps,
    state: { values, errors },
  } = useForm({
    initialState: { id: "", save: false },
    validationSchema: {
      id: {
        required: t("invalid_upi"),
        type: "string",
        when: (value: string) => {
          const upiRegex = /[a-zA-Z0-9.\-_]{2,256}@[a-zA-Z]{2,64}/;
          const isValid = upiRegex.test(value);
          if (!isValid) {
            return {
              status: true,
              message: t("invalid_upi"),
            };
          }
        },
      },
    },
  });

  const openUPIApp = async (upiApp: UpiIntentApps, isQRCode = false) => {
    if (isLoading || !upiApp) return;
    try {
      // Only proceed if we're on mobile and UPI_INTENT is selected
      if (!isMobile() && !isQRCode) {
        errorToast(t("invalid_upi"));
        return;
      }
      setIsLoading(true);
      const response = await getPaymentMethodAction({
        method: "UPI_INTENT",
        checkoutId,
        setCheckoutExpired,
        context: modeData.context,
        useKratosPaymentFlow: Boolean(merchant?.migrationConfig?.kratosPaymentFlow),
      });
      if (!Boolean(response)) {
        return;
      }

      const url = response?.next_steps?.pg_action?.action_url;
      const upiAppLink = getUpiAppLink(isQRCode ? "NA" : upiApp, url ?? "");

      if (!Boolean(upiAppLink)) {
        errorToast(t("something_went_wrong"));
        return;
      }
      const methodData = paymentMethods?.find((e: PaymentMode) => e.name === "UPI_INTENT");

      if (isQRCode) {
        sendAnalyticsEvent({
          eventName: analyticsEvents.FLO_PAYMENT_METHOD_SELECTED,
          eventFor: [analyticsTypes.SF_ANALYTICS, analyticsTypes.FACEBOOK_PIXEL],
          eventType: "click",
          metaData: {
            methodType: "upi_qr",
            automationData: getAutomationPayloadForMethod(methodData),
          },
        });
        sendAnalyticsEvent({
          eventName: analyticsEvents.FLO_PAYMENT_MODE_SELECTED,
          eventType: "click",
          metaData: {
            modeType: upiApp,
            methodType: "upi_qr",
            automationData: getAutomationPayloadForMethod(methodData),
          },
        });

        setGeneratedQRCode(upiAppLink);

        setPaymentState({
          isProcessingDialogVisible: false,
          paymentStatus: "PROCESSING",
          isPolling: true,
          isQRCode: true,
        });
      } else {
        sendAnalyticsEvent({
          eventName: analyticsEvents.FLO_PAYMENT_METHOD_SELECTED,
          eventFor: [analyticsTypes.SF_ANALYTICS, analyticsTypes.FACEBOOK_PIXEL],
          eventType: "click",
          metaData: {
            methodType: "upi_intent",
            automationData: getAutomationPayloadForMethod(methodData),
          },
        });
        sendAnalyticsEvent({
          eventName: analyticsEvents.FLO_PAYMENT_MODE_SELECTED,
          eventType: "click",
          metaData: {
            modeType: upiApp,
            methodType: "upi_intent",
            automationData: getAutomationPayloadForMethod(methodData),
          },
        });

        setPaymentState({
          isProcessingDialogVisible: true,
          paymentStatus: "PROCESSING",
          isPolling: true,
        });

        setIsOtherAppsOpen(false);
        redirectUrl(upiAppLink);
      }
    } catch (e: any) {
      handlePaymentError({
        error: e,
        callbacks: {
          showErrorToast: errorToast,
          getTranslation: t,
          setOOSItems: (items) => setOOSItems(getItems(items, true) ?? []),
          openPopup,
          onOutOfStock: (items) =>
            sendAnalyticsEvent({
              eventName: analyticsEvents.FLO_ITEM_OOS,
              eventFor: [analyticsTypes.SF_ANALYTICS],
              eventType: "click",
              metaData: {
                outOfStockItems: items,
              },
            }),
        },
        payload: {
          checkoutId,
          payment_mode: modeData.name,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const qrCode = useCallback(() => {
    return (
      <div
        className="h-32 w-32 relative -top-1 border p-2 rounded-md"
        style={{ borderColor: parsedColors.borderColor }}>
        {!generatedQRCode ? (
          <>
            <img
              src={BlurredQRImage}
              width={110}
              height={110}
              className="absolute top-0 left-0 w-full h-full p-1.5"
            />
            <button
              onClick={() => upiApps[0] && openUPIApp(toUpiApp(upiApps[0].id), true)}
              className="w-full h-full z-1 absolute left-0 top-0 flex items-center justify-center"
              disabled={isLoading || !upiApps[0]}>
              <span className="flex items-center justify-center p-2 text-xs font-medium text-white rounded-full bg-primary-dark w-fit h-fit">
                {isLoading ? <CircularSpinner /> : "Click to show QR"}
              </span>
            </button>
          </>
        ) : (
          <div className="w-full h-full relative">
            <QRCode
              value={generatedQRCode}
              size={110}
              className={`border`}
              style={{ borderColor: parsedColors.borderColor }}
            />
          </div>
        )}
      </div>
    );
  }, [generatedQRCode, isLoading, timeLeft]);

  useEffect(() => {
    if (generatedQRCode && timeLeft > 0) {
      timer.current = setInterval(() => {
        setTimeLeft((prevTime) => {
          if (prevTime <= 1) {
            setGeneratedQRCode(null);
            return 15 * 60;
          }
          return prevTime - 1;
        });
      }, 1000);
    }

    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [generatedQRCode]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const toUpiApp = (id: string): UpiIntentApps => id?.toUpperCase() as UpiIntentApps;

  const selectApp = (method: UpiIntentItems) => isUpiIntentModeAvailable && openUPIApp(toUpiApp(method?.id));

  const renderAppTile = (method: UpiIntentItems, onSelect: () => void) => (
    <li
      key={toUpiApp(method.id)}
      onClick={onSelect}
      className="flex cursor-pointer items-center justify-center rounded-lg hover:bg-gray-50 py-2">
      <MethodLogo
        id={method.id}
        logo={method.url}
        name={method.name}
        offerText={method.offer_text}
        isLoading={isLoading}
      />
    </li>
  );

  const configuredTitle = methodUiAttributes.title || modeData?.metadata?.title;

  const modeTitle = configuredTitle
    ? configuredTitle
    : isMobile()
    ? t("upi_description_mobile")
    : t("upi_description_desktop");

  const upiIntentTileRenderer = () => {
    return (
      <>
        <div
          className={`relative rounded-xl border divide-y`}
          style={{
            backgroundColor: parsedColors.backgroundColor,
            color: parsedColors.textColor,
            borderColor: parsedColors.borderColor,
            ["--divider-color" as any]: getTextColorByBrightness({
              bgHexColor: parsedColors.backgroundColor ?? "#FFFFFF",
              lightOpacity: 10,
              darkOpacity: 6,
            }),
          }}>
          <div className="flex flex-row items-center justify-between p-3">
            <div className="flex flex-row items-center gap-3">
              <img
                src={`${IMAGE_CDN_URL}/upi-tile.png`}
                alt="upi_logo"
                className={`h-8 w-8 object-contain ${isLoading && "upiLogoLoader-img"}`}
              />
              <div className="flex flex-col gap-1 text-sm font-medium ">
                {modeTitle}
                {hasDiscountOrFreebie && (
                  <OfferBadge
                    text={offerText || ""}
                    backgroundColor={methodUiAttributes.offerBadge.backgroundColor}
                    textColor={methodUiAttributes.offerBadge.textColor}
                  />
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <div className="text-sm font-medium">{upiAmount && currencyFormatter(upiAmount)}</div>
              {/* Adding an empty container just to match the placement of the price with the rest of the cards */}
              <div className="h-3.5 w-3.5">{""}</div>
            </div>
          </div>
          {isMobile() ? (
            <>
              <div className="flex w-full flex-col">
                <ul className="grid w-full auto-cols-fr grid-flow-col gap-2 px-4 pt-4 pb-2">
                  {upiApps?.map((method) => renderAppTile(method, () => selectApp(method)))}
                  {otherApps.length > 0 && (
                    <li
                      key="OTHERS"
                      onClick={() => setIsOtherAppsOpen(true)}
                      className="flex flex-col cursor-pointer items-center justify-center rounded-lg p-2 hover:bg-gray-50 gap-2">
                      <AppsGrid items={otherApps.filter((item) => item.is_active).map((item) => item.url)} />
                      <p className="text-xxs font-medium capitalize !mt-0 opacity-[70%]">{t("upi_others")}</p>
                    </li>
                  )}
                </ul>
              </div>
              {isLoading && <OverlaySpinner />}
            </>
          ) : (
            <>
              <div className="flex w-full gap-6 px-4 pt-4 pb-3">
                <div className="flex items-center justify-center w-1/2">{qrCode()}</div>
                <div className="grid grid-rows-3 items-center justify-between">
                  <p className="text-xs opacity-[0.6]">Scan the QR & Pay with your UPI app of choice</p>
                  <ul className="flex flex-1 h-fit">
                    {upiApps?.slice(0, 4)?.map((method, index) => (
                      <li
                        key={index}
                        className="flex items-center justify-center rounded-full h-fit ml-[-10px] first:ml-0 border border-[rgba(0,0,0,0.10)] bg-white">
                        <img src={method?.url} alt={method?.name} className="w-7 h-7 rounded-full" />
                      </li>
                    ))}
                  </ul>
                  {generatedQRCode && (
                    <div className="flex items-center gap-1.5 text-xs">
                      <p className="font-normal text-nowrap opacity-[0.6]">QR is valid for </p>
                      <span
                        className={`font-medium p-1.5 rounded-md w-[80px] text-center`}
                        style={{
                          backgroundColor: hexToRgba(parsedColors.textColor, 0.1),
                          color: parsedColors.textColor,
                        }}>
                        {formatTime(timeLeft)} mins
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        <GenericDialog
          isOpen={isOtherAppsOpen}
          setIsOpen={setIsOtherAppsOpen}
          translateAxis="y"
          modalType="UPI_INTENT"
          dialogOverlay={true}
          closeOnOverlayClick={false}
          showExternalClose={true}
          customClass="!rounded-t-2xl pb-0">
          <DialogHeader className="border-b border-dotted border-b-[2px] !bg-[rgba(0,0,0,0)_0%] !px-6">
            <p className="w-full py-3 flex items-center text-[18px] font-medium">{t("upi_payment_header")}</p>
          </DialogHeader>
          <DialogBody>
            {/* Hide the suggested apps and other apps section if either is not available */}
            {upiApps && upiApps.length > 0 && (
              <div className="flex flex-col gap-2 bg-[linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.08)_100%)] px-6 pb-2 py-2">
                <p className="text-xs font-medium opacity-60">{t("suggested_apps")}</p>
                <ul className="grid w-full auto-cols-fr grid-flow-col gap-2 pt-2 pb-2">
                  {upiApps?.map((method) => renderAppTile(method, () => selectApp(method)))}
                </ul>
              </div>
            )}

            {otherApps && otherApps.length > 0 && (
              <div className="flex flex-col gap-2 p-4">
                <p className="text-xs font-medium px-2 opacity-60">{t("other_apps")}</p>
                <ul className="flex flex-col">
                  {otherApps.map((app) => (
                    <li
                      key={toUpiApp(app.id)}
                      onClick={() => selectApp(app)}
                      className="flex cursor-pointer items-center gap-3 py-2 px-2 rounded rounded-xl hover:bg-black/[0.04] active:bg-black/[0.04]">
                      <img src={app.url} alt={app.name} className="h-8 w-8 rounded rounded-lg" />
                      <p className="flex-1 text-sm font-medium capitalize opacity-80">{app.name}</p>
                      {app.offer_text && (
                        <p className="text-xs font-medium text-emerald-600">{app.offer_text}</p>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </DialogBody>
        </GenericDialog>
      </>
    );
  };

  return (
    <SuggestedMethodWrapper
      isSuggested={methodUiAttributes.isSuggested}
      title={methodUiAttributes.suggestedTile?.title}
      backgroundColor={methodUiAttributes.suggestedTile?.backgroundColor}
      textColor={methodUiAttributes.suggestedTile?.textColor}>
      {upiIntentTileRenderer()}
    </SuggestedMethodWrapper>
  );
};
